import { useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import '../../css/office-hours.css';

// api / models / redux
import { useAppSelector } from '../../hooks/hooks';
import OfficeHoursApi from '../../api/office-hours.api';
import WeekdaysApi from '../../api/weekdays.api';
import { OfficeHour, OfficeHourBooking, OfficeHourSettings } from '../../models/office-hour';

//comps
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import GlobalButton from '../../components/global/button';
import DataGrid from '../../components/grid/dataGrid';
import OfficeHoursDialog from '../../components/dialogs/officeHoursDialog';
import DeleteDialog from '../../components/dialogs/deleteDialog';
import StudentApppintmentDialog from '../../components/dialogs/studentAppointmentDialog';
import ContentContainer from '../../components/layout/contentContainer';

//icons
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as ClearIcon } from '../../assets/icons/clear-icon.svg';
import { ReactComponent as MailIcon } from '../../assets/icons/mail-icon.svg';
import { ReactComponent as RepeatIcon } from '../../assets/icons/repeat-icon.svg';
import { ReactComponent as HelpIcon } from '../../assets/icons/help-icon.svg';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

// constants
import { colors } from '../../theme';
import { MOMENT_DATE_DISPLAY_FORMAT, MOMENT_DATE_FORMAT, MOMENT_DATETIME_FORMAT, MOMENT_TIME_DISPLAY_FORMAT, MOMENT_TIME_FORMAT } from '../../constants/date_format';

// pdf files
import OfficeHourDoc from '../../assets/guides/BCIT-OfficeHour-Guide.pdf';
import UpcomingOfficeHoursDialog from '../../components/dialogs/upcomingOfficeHoursDialog';
import { Term } from '../../models/term';
import GlobalSwitch from '../../components/global/switch';
import { openConfirmModal } from '../../helpers/openConfirmModal';

interface BookingButtonProps {
    isFull: boolean;
}

const generateEmptyOfficehour = (timeIn, timeOut, termStartDate) => {
    return {
        appointmentNote: null,
        appointmentUrl: null,
        dayOfWeek: null,
        date: moment().isBefore(moment(termStartDate, MOMENT_DATE_FORMAT), 'day') ? moment(termStartDate, MOMENT_DATE_FORMAT) : moment(),
        officeHoursNote: null,
        appointmentRequired: false,
        appointmentSlots: 0,
        id: null,
        timeIn: moment(timeIn, MOMENT_TIME_FORMAT),
        timeOut: moment(timeOut, MOMENT_TIME_FORMAT),
        timeslotLength: null,
        week: 0,
    };
};

const OfficeHours = () => {
    const appState = useAppSelector((state) => state.appState);
    const user = useAppSelector((state) => state.user);
    const { instructorId } = user;
    const { term, termSchools: allTerms } = appState;

    const _officeHoursApi = new OfficeHoursApi();
    const _weekDayApi = new WeekdaysApi();
    const [officeHoursDialog, setOfficeHoursDialog] = useState({
        open: false,
        isEdit: false,
        isSubmitDisabled: false,
    });
    const [upcomingOfficeHoursDialog, setUpcomingOfficeHoursDialog] = useState({
        dataItems: [],
        currentTerm: null,
        isOpen: false,
    });
    const [deleteDialog, setDeleteDialog] = useState({
        dataItem: null,
        isOpen: false,
        deleteAction: null,
    });
    const [appointmentDialog, setAppointmentDialog] = useState({
        dataItem: null,
        isOpen: false,
        appointment: null,
    });
    const [itemInEdit, setItemInEdit] = useState({});
    const [state, setState] = useState({
        officeHours: [],
        upcomingOfficeHours: [],
        weekdays: [],
        roles: [],
        activeCell: null,
        settings: null,
    });
    const [isLoading, setIsLoading] = useState(false);

    //FIXME: awaiting this does not refresh the grid as expected
    const loadOfficeHoursData = async (instructorId: number, termStartDate: string, termEndDate: string) => {
        setIsLoading(true);
        const settings = await _officeHoursApi.GetSettings();
        const officeHours = await _officeHoursApi.GetOfficeHours(instructorId, termStartDate, termEndDate);

        //only load office hours if a term is available to the user past the current term
        const latestTerm = getLatestTerm();
        let upcomingOfficeHours = [];
        if (moment(latestTerm.startDate).isAfter(moment(term.endDate))) {
            upcomingOfficeHours = await _officeHoursApi.GetOfficeHours(instructorId, moment(term.endDate).add(1, 'days').format(MOMENT_DATETIME_FORMAT), '2099-12-31T00:00:00');
        }

        const weekdays = await _weekDayApi.GetAll();
        const roles = await _officeHoursApi.GetRoles();

        setState((prevState) => ({
            ...prevState,
            officeHours,
            upcomingOfficeHours,
            weekdays,
            roles,
            settings,
        }));
    };

    useEffect(() => {
        if (term?.termId && instructorId)
            loadOfficeHoursData(instructorId, term.startDate, term.endDate)
                .catch((error) => {
                    Sentry.captureException(error);
                    toast.error('Error loading office hours');
                })
                .finally(() => setIsLoading(false));

        if (term && !instructorId) toast.error('Invalid instructor ID. Please contact support');
    }, [term, user]);

    const createOfficeHour = () => {
        const officeHour = generateEmptyOfficehour('08:00:00', '09:00:00', appState.term.startDate);
        setItemInEdit(officeHour);
        setOfficeHoursDialog({ open: true, isEdit: false, isSubmitDisabled: false });
    };

    const editOfficeHour = (data) => {
        setItemInEdit(data);
        setOfficeHoursDialog({ open: true, isEdit: true, isSubmitDisabled: false });
        setState({ ...state, activeCell: data });
    };

    const closeOfficeHourModal = () => {
        setOfficeHoursDialog({ open: false, isEdit: false, isSubmitDisabled: false });
        setState({ ...state, activeCell: null });
    };

    const handleUpcomingOfficeHoursDialog = (dataItems?: Array<OfficeHour>, currentTerm?: Term) => {
        setUpcomingOfficeHoursDialog({
            ...upcomingOfficeHoursDialog,
            dataItems,
            currentTerm,
            isOpen: !upcomingOfficeHoursDialog.isOpen,
        });
    };

    const handleDeleteDialog = (dataItem?: OfficeHour, deleteAction?: string) => {
        setDeleteDialog({
            ...deleteDialog,
            dataItem,
            isOpen: !deleteDialog.isOpen,
            deleteAction: deleteAction && deleteAction,
        });
    };

    const handleAppointmentDialog = (appointment?: OfficeHourBooking) => {
        setAppointmentDialog({ ...appointmentDialog, isOpen: !appointmentDialog.isOpen, appointment });
    };

    const getLatestTerm = () => {
        return allTerms.reduce((latest, current) => {
            return moment(current.endDate).isAfter(moment(latest.endDate)) ? current : latest;
        }, allTerms[0]);
    };

    const addOfficeHours = async (officeHour: OfficeHour) => {
        let payload = { ...officeHour };

        // One-day Office Hours
        if (!officeHour?.isRecurring) {
            payload = { ...payload, endDate: null };
        }

        try {
            setIsLoading(true);
            setOfficeHoursDialog({ ...officeHoursDialog, isSubmitDisabled: true });

            const latestTerm = getLatestTerm();

            if (moment(officeHour.startDate).isAfter(moment(latestTerm.endDate))) {
                toast.error('Office Hour should be within the latest term in the system');
                setOfficeHoursDialog({ ...officeHoursDialog, isSubmitDisabled: false });
                return;
            }

            const isSuccess = await _officeHoursApi.AddOfficeHour(payload);
            if (isSuccess) {
                loadOfficeHoursData(instructorId, term?.startDate, term?.endDate);
                toast.success(`Office Hour successfully created.`);
                closeOfficeHourModal();
            } else {
                toast.warn('Office Hours not created.');
                closeOfficeHourModal();
            }
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setContext('data', {
                    instructorId: instructorId,
                    officeHour: officeHour,
                });

                Sentry.captureException(error);
            });
            toast.error('Error creating office hours');
            setOfficeHoursDialog({ ...officeHoursDialog, isSubmitDisabled: false });
        } finally {
            setIsLoading(false);
        }
    };

    const saveOfficeHours = async (officeHour: OfficeHour) => {
        let payload = { ...officeHour, instructorId: instructorId };
        // One-day Office Hours
        if (!officeHour?.isRecurring) {
            payload = { ...payload, endDate: null };
        }
        if (!officeHoursDialog.isEdit) {
            await addOfficeHours(payload);
        } else {
            try {
                setIsLoading(true);
                setOfficeHoursDialog({ ...officeHoursDialog, isSubmitDisabled: true });

                const latestTerm = getLatestTerm();

                if (moment(officeHour.startDate).isAfter(moment(latestTerm.endDate))) {
                    toast.error('Attempted to create Office Hours past the latest term in the system');
                    setOfficeHoursDialog({ ...officeHoursDialog, isSubmitDisabled: false });
                    return;
                }

                const isUpdated = await _officeHoursApi.UpdateOfficeHour(payload);
                if (isUpdated) {
                    loadOfficeHoursData(instructorId, term?.startDate, term?.endDate);
                    toast.success(`Changes have been successfully saved.`);
                    closeOfficeHourModal();
                } else {
                    toast.warn('Office Hours not updated.');
                    closeOfficeHourModal();
                }
            } catch (error) {
                Sentry.withScope((scope) => {
                    scope.setContext('data', {
                        instructorId: instructorId,
                        officeHour: officeHour,
                    });

                    Sentry.captureException(error);
                });
                toast.error('Error updating office hour');
                setOfficeHoursDialog({ ...officeHoursDialog, isSubmitDisabled: false });
            } finally {
                setIsLoading(false);
            }
        }
    };

    const deleteOfficeHour = async () => {
        try {
            const response = await _officeHoursApi.DeleteOfficeHour(instructorId, deleteDialog.dataItem?.id);
            if (response.totalAppointmentsDeleted) {
                toast.success(`${response.totalAppointmentsDeleted} future appointments deleted. The bookers are notified of this cancellation.`, { autoClose: false });
            }
            loadOfficeHoursData(instructorId, term?.startDate, term?.endDate);
            toast.success(`Office Hour has been successfully deleted.`);
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setContext('data', {
                    instructorId: instructorId,
                    dataItem: deleteDialog.dataItem,
                });

                Sentry.captureException(error);
            });
            toast.error('Error deleting office hour');
        } finally {
            handleDeleteDialog();
            setIsLoading(false);
        }
    };

    const deleteAllOfficeHours = async (instructorId: number, startDate: string, endDate: string) => {
        try {
            const response = await _officeHoursApi.DeleteAllOfficeHours(instructorId, startDate, endDate);

            if (response.totalAppointmentsDeleted) {
                toast.success(`${response.totalAppointmentsDeleted} future appointments deleted. The bookers are notified of this cancellation.`, { autoClose: false });
            }

            loadOfficeHoursData(instructorId, startDate, endDate);
            toast.success(`All Office Hours have been successfully deleted.`);
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setContext('data', {
                    instructorId: instructorId,
                    startDate: startDate,
                    endDate: endDate,
                });

                Sentry.captureException(error);
            });
            toast.error('Error deleting all office hours');
        } finally {
            handleDeleteDialog();
            setIsLoading(false);
        }
    };

    const loadTimeSlotBookings = async (officeHourId: number, setExpandableRow: any) => {
        try {
            const bookings = await _officeHoursApi.GetTimeSlotBooking(officeHourId);
            setExpandableRow(bookings);
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error loading bookings');
        }
    };

    const handleRedirectToPublicSite = () => {
        const { termSchoolId } = term;

        if (!instructorId) {
            toast.error('Your instructor id is not setup correctly');
            return;
        }

        const school = appState.school.name.toLowerCase();
        const link =
            window.location.hostname === 'timetablesadmin.bcit.ca'
                ? `https://timetables.bcit.ca/${school}/instructor/${termSchoolId}/${instructorId}`
                : `https://timetablestest.bcit.ca/${school}/instructor/${termSchoolId}/${instructorId}`;
        window.open(link, '_blank');
    };

    const handleOpenOHGuide = () => {
        window.open(OfficeHourDoc);
    };

    const handleSettingsChange = async (name: string, isChecked: boolean) => {
        try {
            const settings = {
                disallowBookingWithin24Hours: isChecked,
            } as OfficeHourSettings;

            await _officeHoursApi.UpdateSettings(settings);

            toast.success(`Settings updated.`);

            setState((prevState) => ({
                ...prevState,
                settings: {
                    ...prevState.settings,
                    disallowBookingWithin24Hours: isChecked,
                },
            }));
        } catch {
            toast.error('Error updating office hour settings.');
        }
    };

    const renderGrid = (setExpandableRow, data, expandedRow, setExpandedRow, onChange) => {
        const { dayOfWeek, startDate, timeIn, timeOut, publicNote, appointmentRequired, appointmentSlotCount, bookedSlotsCount, id } = data;

        const expandRow = (availableSlots, expandedRow, setExpandedRow) => {
            if (availableSlots && availableSlots > 0) {
                if (id !== expandedRow) {
                    loadTimeSlotBookings(id, setExpandableRow);
                    // TODO: only open 1 row at a time
                    setExpandedRow(id);
                    setState({ ...state, activeCell: data });
                } else {
                    setExpandedRow();
                    setState({ ...state, activeCell: null });
                }
            }
        };

        // Get the week number based on startDate
        const termStartDate = moment(term?.startDate);
        const officeHourDate = moment(startDate);
        const totalDays = officeHourDate.diff(termStartDate, 'days');
        const week = Math.floor(totalDays / 7 + 1);
        const hasDatePassed = moment().isAfter(officeHourDate, 'day');

        return (
            <>
                <TableCell
                    sx={{
                        width: '3%',
                        textAlign: 'center',
                        color: hasDatePassed && 'rgba(51, 51, 51, 0.6)',
                    }}
                >
                    {week}
                </TableCell>
                <TableCell sx={{ width: '12%', color: hasDatePassed && 'rgba(51, 51, 51, 0.6)' }}>{dayOfWeek}</TableCell>
                <TableCell
                    sx={{
                        width: '12%',
                        color: hasDatePassed && 'rgba(51, 51, 51, 0.6)',
                    }}
                >
                    {moment(startDate).format(MOMENT_DATE_DISPLAY_FORMAT)}
                </TableCell>
                <TableCell
                    sx={{
                        width: '10%',
                        color: hasDatePassed && 'rgba(51, 51, 51, 0.6)',
                    }}
                >
                    {moment(timeIn, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}
                </TableCell>
                <TableCell
                    sx={{
                        width: '10%',
                        color: hasDatePassed && 'rgba(51, 51, 51, 0.6)',
                    }}
                >
                    {moment(timeOut, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}
                </TableCell>
                <TableCell
                    sx={{
                        color: hasDatePassed && 'rgba(51, 51, 51, 0.6)',
                        svg: { path: { fill: colors.green.main } },
                    }}
                >
                    {appointmentRequired ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <RepeatIcon style={{ marginRight: '5px' }} />
                            Appointment-based
                        </div>
                    ) : (
                        'Drop-In'
                    )}
                </TableCell>
                <TableCell sx={{ width: '20%', color: hasDatePassed && 'rgba(51, 51, 51, 0.6)' }}>{publicNote}</TableCell>
                <TableCell
                    onClick={() => {
                        if (!appointmentRequired) return;
                        else {
                            // setExpandedRow(id);
                            expandRow(appointmentSlotCount, expandedRow, setExpandedRow);
                            setAppointmentDialog({ ...appointmentDialog, dataItem: data });
                        }
                    }}
                >
                    {appointmentRequired && (
                        <BookingButton isFull={bookedSlotsCount == appointmentSlotCount}>
                            {bookedSlotsCount ? bookedSlotsCount : 0} / {appointmentSlotCount ? appointmentSlotCount : 0}
                            {expandedRow === id ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </BookingButton>
                    )}
                </TableCell>
                <CommandTableCell sx={{ width: '6%' }}>
                    <EditIcon
                        id="edit"
                        style={{
                            opacity: hasDatePassed && '0.5',
                            pointerEvents: hasDatePassed && 'all',
                            cursor: hasDatePassed && 'not-allowed',
                        }}
                        onClick={() => {
                            if (!hasDatePassed) editOfficeHour(data);
                        }}
                    />
                    <DeleteIcon
                        id="clear"
                        style={{
                            opacity: hasDatePassed && '0.5',
                            pointerEvents: hasDatePassed && 'all',
                            cursor: hasDatePassed && 'not-allowed',
                        }}
                        onClick={() => {
                            if (!hasDatePassed) handleDeleteDialog(data);
                        }}
                    />
                </CommandTableCell>
            </>
        );
    };

    const renderExpandable = (officeHourData, data, setExpandableRow) => {
        const hasDatePassed = moment().isAfter(officeHourData.startDate, 'day');

        const generateMailToLink = (appointment) => {
            return `mailto:${appointment.email}`;
        };

        // --------------- Time Slot Booking API ---------------

        const createTimeSlotBooking = async (booking: OfficeHourBooking) => {
            try {
                await _officeHoursApi.AddTimeSlotBooking(booking);
                await loadTimeSlotBookings(booking.ohId, setExpandableRow);
                toast.success(`An appointment has been successfully booked for ${booking.name}.`);

                const officeHours = await _officeHoursApi.GetOfficeHours(instructorId, term.startDate, term.endDate);
                setState({
                    ...state,
                    officeHours,
                });
            } catch (error) {
                Sentry.captureException(error);
                toast.error(`Error creating appointment.`);
            } finally {
                handleAppointmentDialog();
                setIsLoading(false);
            }
        };

        const saveTimeSlotBooking = async (booking: OfficeHourBooking) => {
            try {
                await _officeHoursApi.UpdateTimeSlotBooking(booking);
                await loadTimeSlotBookings(booking.ohId, setExpandableRow);
                toast.success(`Changes have been successfully saved.`);
            } catch (error) {
                Sentry.captureException(error);
                toast.error('Error saving booking');
            } finally {
                handleAppointmentDialog();
                setIsLoading(false);
            }
        };

        const cancelTimeSlotBooking = async () => {
            try {
                await _officeHoursApi.DeleteTimeSlotBooking(deleteDialog.dataItem?.id);
                await loadTimeSlotBookings(deleteDialog.dataItem?.ohId, setExpandableRow);
                toast.success('Appointment has been successfully cancelled');

                const officeHours = await _officeHoursApi.GetOfficeHours(instructorId, term.startDate, term.endDate);
                setState({
                    ...state,
                    officeHours,
                });
            } catch (error) {
                Sentry.captureException(error);
                toast.error('Error canceling booking');
            } finally {
                handleDeleteDialog();
                setIsLoading(false);
            }
        };

        return (
            <>
                <Table size="small" sx={{ border: `1px solid ${colors.grey.light}` }}>
                    <TableHead sx={{ th: { borderBottom: `1px solid ${colors.grey.light}` } }}>
                        <TableCell>Time In</TableCell>
                        <TableCell>Time Out</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>BCIT ID</TableCell>
                        <TableCell>Topic</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell></TableCell>
                    </TableHead>
                    <TableBody sx={{ opacity: hasDatePassed && `0.5` }}>
                        {data.length > 0 &&
                            data.map((booking, i) => {
                                return (
                                    <TableRow key={i}>
                                        <TableCell sx={{ width: '7%' }}>{moment(booking.timeIn, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}</TableCell>
                                        <TableCell sx={{ width: '8%' }}>{moment(booking.timeOut, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}</TableCell>
                                        <TableCell sx={{ width: '12%' }}>{booking.name}</TableCell>
                                        <TableCell sx={{ width: '18%' }}>{booking.email}</TableCell>
                                        <TableCell sx={{ width: '13%' }}>{booking.bcitId}</TableCell>
                                        <TableCell sx={{ width: '20%' }}>{booking.topic}</TableCell>
                                        <TableCell sx={{ width: '13%' }}>{booking.role?.name}</TableCell>
                                        {booking.id === 0 ? (
                                            <TableCell>
                                                <GlobalButton
                                                    disabled={hasDatePassed}
                                                    text="add"
                                                    variant="round"
                                                    secondary={'true'}
                                                    startIcon={<PlusIcon />}
                                                    onClick={() => handleAppointmentDialog(booking)}
                                                />
                                            </TableCell>
                                        ) : (
                                            <CommandTableCell>
                                                {hasDatePassed ? (
                                                    <>
                                                        <MailIcon style={{ pointerEvents: 'all', cursor: 'not-allowed' }} id="mail" />
                                                        <EditIcon style={{ pointerEvents: 'all', cursor: 'not-allowed' }} id="edit" />
                                                        <ClearIcon style={{ pointerEvents: 'all', cursor: 'not-allowed' }} id="clear" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <Tooltip title="Mail" arrow>
                                                            <a href={generateMailToLink(booking)}>
                                                                <MailIcon id="mail" />
                                                            </a>
                                                        </Tooltip>
                                                        <Tooltip title="Edit" arrow>
                                                            <EditIcon id="edit" onClick={() => handleAppointmentDialog(booking)} />
                                                        </Tooltip>
                                                        <Tooltip title="Cancel" arrow>
                                                            <ClearIcon id="clear" onClick={() => handleDeleteDialog(booking, 'cancelAppt')} />
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </CommandTableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
                {appointmentDialog.isOpen && (
                    <StudentApppintmentDialog
                        onClose={handleAppointmentDialog}
                        dataItem={appointmentDialog.dataItem}
                        appointment={appointmentDialog.appointment}
                        onCreate={createTimeSlotBooking}
                        onSave={saveTimeSlotBooking}
                        roles={state.roles}
                    />
                )}
                {deleteDialog.deleteAction === 'cancelAppt' && deleteDialog.isOpen && (
                    <DeleteDialog
                        isWarning
                        isCancel
                        warningText={
                            'This will automatically cancel the selected appointment. Any existing appointments will be cancelled and cancellation emails will be sent to those who have booked them.'
                        }
                        deleteItem={'the selected booking'}
                        buttonText={'confirm'}
                        onClose={handleDeleteDialog}
                        onDelete={cancelTimeSlotBooking}
                    />
                )}
            </>
        );
    };

    return (
        <>
            <FlexBox sx={{ justifyContent: 'space-between' }}>
                <div>
                    <GlobalButton variant="contained" text="create" secondary={'true'} startIcon={<PlusIcon />} sx={{ marginRight: '10px' }} onClick={createOfficeHour} />
                    <GlobalButton variant="contained" text="delete all" buttoncolor={colors.red} startIcon={<DeleteIcon />} onClick={() => handleDeleteDialog(null, 'deleteAll')} />
                </div>
                <div>
                    <GlobalSwitch
                        key={'disallowBookingWithin24Hours'}
                        checked={state.settings?.disallowBookingWithin24Hours}
                        name="disallowBookingWithin24Hours"
                        onChange={(name: string, value: boolean) => handleSettingsChange(name, value)}
                        label={'Disallow Bookings within 24 Hours'}
                    />
                    {state.upcomingOfficeHours.length !== 0 && (
                        <InfoBox>
                            <InfoIcon />
                            <Typography variant="body2">You have office hours in upcoming terms</Typography>
                            <GlobalButton
                                sx={{
                                    marginLeft: '2px',
                                    padding: '0 !important',
                                    '& > h5': { fontWeight: '700 !important   ' },
                                }}
                                text="review"
                                onClick={() => handleUpcomingOfficeHoursDialog(state.upcomingOfficeHours, appState.term)}
                            />
                        </InfoBox>
                    )}
                    <GlobalButton variant="outlined" text="office hours guide" startIcon={<HelpIcon />} sx={{ marginRight: '10px' }} onClick={handleOpenOHGuide} />
                    <GlobalButton variant="outlined" text="view on public" onClick={handleRedirectToPublicSite} />
                </div>
            </FlexBox>
            {state.officeHours.length === 0 && isLoading ? (
                <ContentContainer padding={'0px 25px'}>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit
                    isDelete
                    expandable
                    topHeight={5}
                    padding={'7px'}
                    dataItemKey={'id'}
                    columns={GridColums}
                    data={state.officeHours}
                    renderRows={renderGrid}
                    renderExpandable={renderExpandable}
                    activeCell={state.activeCell}
                />
            )}

            {upcomingOfficeHoursDialog.isOpen && (
                <UpcomingOfficeHoursDialog
                    dataItems={upcomingOfficeHoursDialog.dataItems}
                    allTerms={allTerms}
                    currentTerm={upcomingOfficeHoursDialog.currentTerm}
                    onClose={handleUpcomingOfficeHoursDialog}
                />
            )}

            {officeHoursDialog.open && (
                <OfficeHoursDialog
                    isEdit={officeHoursDialog.isEdit}
                    officeHourData={itemInEdit}
                    defaultTimeIn={'08:30:00'}
                    termCode={appState.term.termCode}
                    termStartDate={appState.term.startDate}
                    termEndDate={appState.term.endDate}
                    onClose={closeOfficeHourModal}
                    weekdays={state.weekdays}
                    onSubmit={saveOfficeHours}
                    isSubmitDisabled={officeHoursDialog.isSubmitDisabled}
                />
            )}

            {deleteDialog.isOpen && deleteDialog.deleteAction !== 'cancelAppt' && (
                <DeleteDialog
                    isWarning
                    deleteItem={deleteDialog?.deleteAction === 'deleteAll' ? 'all Office Hours' : 'the selected Office Hour slot'}
                    buttonText={deleteDialog?.deleteAction === 'cancelAppt' ? 'confirm' : 'delete'}
                    onClose={handleDeleteDialog}
                    onDelete={() => {
                        if (deleteDialog?.deleteAction === 'deleteAll') deleteAllOfficeHours(instructorId, term?.startDate, term?.endDate);
                        else deleteOfficeHour();
                    }}
                />
            )}
        </>
    );
};

export default OfficeHours;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
    }
`;

const BookingButton = styled.div<BookingButtonProps>`
    text-align: center;
    border-radius: 4px;
    color: ${(props) => (props.isFull ? colors.white : colors.blue.main)};
    cursor: pointer;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    padding-left: 4px;
    background-color: ${(props) => (props.isFull ? 'rgba(0, 133, 230)' : 'rgba(0, 133, 230, 0.1)')};

    svg {
        padding-left: 4px;
    }
`;

const CommandTableCell = styled(TableCell)`
    && {
        svg {
            width: 20px;
            height: auto;

            path {
                fill: ${colors.grey.medium};
            }
        }

        &:hover {
            cursor: pointer;
        }

        #mail {
            width: 22px;
            margin-right: 8px;

            &:hover {
                path {
                    fill: ${colors.green.main};
                }
            }
        }

        #edit {
            &:hover {
                path {
                    fill: ${colors.blue.main};
                }
            }
        }

        #clear {
            width: 22px;
            margin-left: 5px;

            &:hover {
                path {
                    fill: ${colors.red.main};
                }
            }
        }
    }
`;

const InfoBox = styled.div`
    background-color: ${colors.blue.light};
    color: ${colors.blue.main};
    display: inline-flex;
    vertical-align: middle;
    align-items: center;
    margin-left: 15px;
    margin-right: 15px;
    padding: 2px 5px;
    border-radius: 0px 5px 5px 0px;
    border-left: 3px solid ${colors.blue.main};

    svg {
        margin-right: 3px;

        path {
            fill: ${colors.blue.main};
        }
    }
`;

const GridColums = [
    {
        label: 'Week',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
    {
        id: 'dayOfWeek',
        label: 'Day of Week',
    },
    {
        id: 'startDate',
        label: 'Date',
    },
    {
        id: 'timeIn',
        label: 'Time in',
    },
    {
        id: 'timeOut',
        label: 'Time out',
    },
    {
        id: 'appointmentRequired',
        label: 'Type',
    },
    {
        id: 'publicNote',
        label: 'Public Notes',
    },
    {
        label: 'Bookings',
        sx: {
            width: '86px',
        },
        disabled: true,
    },
];
